import { useState, useEffect, useRef, useCallback } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { financialInstitutionsApi } from '../lib/api'

export interface Institution {
  id: string
  name: string
  nameKana: string | null
  category: string
  sortOrder: number
  /** コーポレートカラー。呼び出し側でカラーの初期値に使う。未定義の金融機関は null */
  brandColor: string | null
}

interface Props {
  value: string
  /**
   * 第2引数には、選んだ（または入力した名前と完全一致した）金融機関マスタの行が入る。
   * ブランドカラーを拾いたい呼び出し側が使う。自由入力で一致しなければ undefined
   */
  onChange: (value: string, institution?: Institution) => void
  token: string
  placeholder?: string
  className?: string
  filterCategory?: string
}

export function InstitutionCombobox({ value, onChange, token, placeholder, className, filterCategory }: Props) {
  const { t } = useLanguage()
  const [query, setQuery] = useState(value)
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const fetchedRef = useRef(false)

  // Sync external value to query when form resets or initial value is set
  useEffect(() => {
    setQuery(value)
  }, [value])

  // Fetch all institutions once on first open
  const fetchAll = useCallback(async () => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    setLoading(true)
    try {
      const res = await financialInstitutionsApi.list(token)
      setInstitutions(res.data)
    } catch {
      // ignore — user can still type freely
    } finally {
      setLoading(false)
    }
  }, [token])

  const handleFocus = () => {
    setOpen(true)
    void fetchAll()
  }

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
    setQuery(v)
    // 自由入力でも、名前がマスタと完全一致したら選んだのと同じ扱いにする
    onChange(v, institutions.find((fi) => fi.name === v))
    setOpen(true)
  }

  const handleSelect = (institution: Institution) => {
    setQuery(institution.name)
    onChange(institution.name, institution)
    setOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false)
  }

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filtered = (() => {
    let list = filterCategory
      ? institutions.filter((fi) => fi.category === filterCategory)
      : institutions
    if (query.length > 0) {
      list = list.filter(
        (fi) => fi.name.includes(query) || (fi.nameKana ?? '').includes(query)
      )
    }
    return list
  })()

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={handleInput}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto dark:border-gray-600 dark:bg-gray-800">
          {loading && (
            <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">{t('common_loading')}</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">{t('institution_no_result')}</div>
          )}
          {!loading && filtered.map((fi) => (
            <button
              key={fi.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                handleSelect(fi)
              }}
              className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-indigo-50 dark:text-gray-200 dark:hover:bg-indigo-950 transition-colors"
            >
              {fi.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
